import React, { useEffect, useMemo } from 'react';
import { titleEditorStoreSelector, useTitleEditorStore } from '@warehouse/title/domain';
import {
	createRecordPathCodec,
	IframeEmbedParams,
	QuickbaseIFrameContainer,
	quickbaseTabStoreSelector,
	recordPathSegmentFor,
	recordPresentationFor,
	TabType,
	useNxInventoryStore,
	useQuickbaseHostBridgeSync,
} from '@warehouse/quickbase';

export function NxInventory() {
	const title = useTitleEditorStore(titleEditorStoreSelector.title);

	// Store selectors
	const isLoading = useNxInventoryStore(quickbaseTabStoreSelector.isLoading);
	const isAuthenticated = useNxInventoryStore(quickbaseTabStoreSelector.isAuthenticated);
	const error = useNxInventoryStore(quickbaseTabStoreSelector.error);
	const embedUrl = useNxInventoryStore(quickbaseTabStoreSelector.embedUrl);
	const frameUrl = useNxInventoryStore(quickbaseTabStoreSelector.frameUrl);
	const showAuthOverlay = useNxInventoryStore(quickbaseTabStoreSelector.showAuthOverlay);
	const authFlow = useNxInventoryStore(quickbaseTabStoreSelector.authFlow);
	const actions = useNxInventoryStore(quickbaseTabStoreSelector.actions);

	const { authenticate, loadEmbedUrl, setShowAuthOverlay, dismissAuthOverlay, recoverSession } = actions;

	const codec = useMemo(
		() =>
			createRecordPathCodec({
				resolveBasePath: (pathname) => pathname.match(/^\/title\/[^/]+/)?.[0] ?? null,
				recordSegment: recordPathSegmentFor(TabType.NxInventory),
			}),
		[],
	);

	const { bridge, initialEmbedParams, reloadToken } = useQuickbaseHostBridgeSync({
		embedUrl,
		codec,
		reloadEmbedUrl: (embedParams: IframeEmbedParams) => {
			if (title?.uuid) loadEmbedUrl(title.uuid, embedParams);
		},
		presentation: recordPresentationFor(TabType.NxInventory),
	});

	// Load embed URL when title changes
	useEffect(() => {
		if (title?.uuid) {
			loadEmbedUrl(title.uuid, initialEmbedParams);
		}
	}, [title?.uuid, loadEmbedUrl, initialEmbedParams]);

	return (
		<QuickbaseIFrameContainer
			isLoading={isLoading}
			isAuthenticated={isAuthenticated}
			error={error}
			embedUrl={embedUrl}
			frameUrl={frameUrl}
			showAuthOverlay={showAuthOverlay}
			authenticate={authenticate}
			setShowAuthOverlay={setShowAuthOverlay}
			dismissAuthOverlay={dismissAuthOverlay}
			recoverSession={recoverSession}
			authFlow={authFlow}
			iFrameType="NxInventory"
			bridge={bridge}
			reloadToken={reloadToken}
		/>
	);
}
